import { Flex, Text, useDisclosure } from '@chakra-ui/react';
import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { needsInputTypeDataLossWarning } from 'components/Modals/InputTypeDataLossWarningModal';
import { STATUS } from 'projectConstants';
import { useAuthContext } from 'context/authContext';
import { getSharedModelUsers } from 'redux/HeaderSlice';
import { getIndicatorDetailStatus, getModelTime, setTabShimmer } from 'redux/IndicatorPageSlice';
import { UpdatePlangPageIndicatorTableData } from 'redux/PlanPageSlice';
import { getBaseScenario } from 'redux/ScenarioSlice';
import { AppDispatch } from 'utils/GlobalHelpers';
import {
  PLAN_REVAMP_RADIUS_SM,
  PLAN_SEGMENT_TRACK_BG,
  PLAN_TEXT,
  accent,
  neutral,
  text as textToken,
} from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';

import ModernInputTypeDataLossWarningModal from './ModernInputTypeDataLossWarningModal';

interface Properties {
  inputDimensions: { id: string }[];
  inputType: string;
  indicatorId: number;
  blockId: number;
  refetchDrivers: () => void;
  refetchRequest: () => void;
  inputDataValues: Record<string, string>[];
  timeDimension: number;
  timeRange: string[];
  timeRangeYears: string[];
}

const InputTypeGroupedButton = ({ inputType, indicatorId, refetchRequest }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('');
  const {
    isOpen: isDataLossWarningOpen,
    onOpen: onDataLossWarningOpen,
    onClose: onDataLossWarningClose,
  } = useDisclosure();
  const [pendingInputType, setPendingInputType] = useState<string | null>(null);
  const modelTime = useSelector(getModelTime);
  const timeGranularity = modelTime?.properties?.time_granularity.toLowerCase();
  const indicatorDetailStatus = useSelector(getIndicatorDetailStatus);
  const baseScenario = useSelector(getBaseScenario);
  const [isAPiLoading, setApiLoading] = useState(false);

  const { user } = useAuthContext();
  const shareModelUsers = useSelector(getSharedModelUsers);

  const currentUserPermissions = shareModelUsers.find((sharedUser: any) => {
    return user && sharedUser?.user_id === user?.id;
  });

  const disableChange =
    indicatorDetailStatus !== STATUS.SUCCESS ||
    isAPiLoading ||
    currentUserPermissions?.role === 'view';

  const performInputTypeChange = useCallback(
    async (newType: string) => {
      if (activeTab === newType) return;
      setApiLoading(true);
      const payloadNew: any = {
        indicator_id: indicatorId,
        request_body: {
          type: newType,
          scenario_id: baseScenario?.id,
          bulk_input: true,
          format_excel_data: true,
        },
      };
      setActiveTab(newType);
      dispatch(setTabShimmer(true));
      await dispatch(UpdatePlangPageIndicatorTableData(payloadNew));
      setTimeout(() => {
        setApiLoading(false);
      }, 600);
      refetchRequest();
    },
    [activeTab, indicatorId, baseScenario?.id, dispatch, refetchRequest],
  );

  const performInputTypeChangeReference = useRef(performInputTypeChange);
  performInputTypeChangeReference.current = performInputTypeChange;

  const debouncedInputTypeChange = useMemo(
    () =>
      debounce((newType: string) => {
        performInputTypeChangeReference.current(newType).catch(() => {});
      }, 1000),
    [],
  );

  useEffect(
    () => () => {
      debouncedInputTypeChange.cancel();
    },
    [debouncedInputTypeChange],
  );

  const requestInputTypeChange = (newType: string) => {
    if (activeTab === newType || disableChange) return;
    if (needsInputTypeDataLossWarning(activeTab, newType)) {
      setPendingInputType(newType);
      onDataLossWarningOpen();
      return;
    }
    debouncedInputTypeChange(newType);
  };

  const handleConfirmDataLossChange = () => {
    if (pendingInputType != null) {
      performInputTypeChange(pendingInputType).catch(() => {});
    }
    setPendingInputType(null);
    onDataLossWarningClose();
  };

  const handleCancelDataLossChange = () => {
    setPendingInputType(null);
    onDataLossWarningClose();
  };

  useEffect(() => {
    if (timeGranularity === 'y' && inputType === 'raw') {
      debouncedInputTypeChange('constant_by_year');
    } else {
      setActiveTab(inputType);
    }
  }, [inputType, timeGranularity, debouncedInputTypeChange]);
  const tabs: Array<{ key: string; label: string; visible: boolean }> = [
    { key: 'constant', label: 'Constant', visible: true },
    { key: 'constant_by_year', label: 'Yearly', visible: true },
    { key: 'raw', label: 'Monthly', visible: timeGranularity === 'm' },
    { key: 'growth', label: 'Growth rate', visible: true },
  ];

  return (
    <>
      {/* Segmented pill with a solid-fill active chip — same track/border
          recipe as the Plan page's M/Q/Y control (PlanTimeFilterSegment),
          but the active tab is a filled accent chip (white text) rather than
          a white chip with accent text, for more visual weight on a row
          this prominent. */}
      <Flex
        alignItems='center'
        bg={PLAN_SEGMENT_TRACK_BG}
        border='1px solid'
        borderColor={neutral.border}
        borderRadius={PLAN_REVAMP_RADIUS_SM}
        p='2px'
        gap='2px'
        w='fit-content'
        maxW='full'
        flexWrap='wrap'
      >
        {tabs
          .filter((tab) => tab.visible)
          .map((tab) => {
            const isActive = activeTab === tab.key;

            return (
              <Flex
                key={tab.key}
                as='button'
                type='button'
                disabled={disableChange}
                alignItems='center'
                justifyContent='center'
                h='28px'
                px='12px'
                borderRadius='6px'
                bg={isActive ? accent.solid : 'transparent'}
                cursor={disableChange ? 'not-allowed' : 'pointer'}
                opacity={disableChange ? 0.6 : 1}
                transition='background 0.15s ease'
                _hover={isActive || disableChange ? undefined : { bg: neutral.surfaceHover }}
                onClick={() => requestInputTypeChange(tab.key)}
              >
                <Text
                  {...PLAN_TEXT.labelSmMedium}
                  color={isActive ? 'white' : textToken.secondary}
                  fontWeight={isActive ? 700 : 500}
                  lineHeight='1'
                  whiteSpace='nowrap'
                >
                  {tab.label}
                </Text>
              </Flex>
            );
          })}
      </Flex>
      <ModernInputTypeDataLossWarningModal
        isOpen={isDataLossWarningOpen}
        onCancel={handleCancelDataLossChange}
        onConfirm={handleConfirmDataLossChange}
      />
    </>
  );
};
export default InputTypeGroupedButton;
